import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import {
    getCompanyPriceHistory,
    refreshCompanyPrice,
    unwrapCompanyPriceResponse,
} from '../services/companyPriceService';
import { formatMarketCapFromRupees } from '../utils/financialUnit';
import { computePriceStats, formatVolume } from '../utils/priceChartStats';
import '../styles/PriceChartTab.css';

const PERIODS = ['1M', '3M', '6M', '1Y', '5Y', 'MAX'];
const DEFAULT_PERIOD = '1Y';

const fmtPrice = (v) => {
    if (v == null || Number.isNaN(Number(v))) return '—';
    return Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

const fmtPct = (v) => {
    if (v == null || Number.isNaN(Number(v))) return '—';
    const n = Number(v);
    return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
};

const formatTickDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return String(dateStr).slice(0, 10);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
};

const formatUpdated = (iso) => {
    if (!iso) return 'unknown';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const toChartRows = (raw) => {
    let result = unwrapCompanyPriceResponse(raw);
    if (Array.isArray(result)) {
        result = { data: result };
    } else if (result?.data && !Array.isArray(result.data) && Array.isArray(result.data.data)) {
        result = result.data;
    }
    const rows = Array.isArray(result?.data) ? result.data : [];
    return {
        result,
        chartData: rows
            .filter((d) => Number.isFinite(Number(d?.close)))
            .map((d) => ({
                date: d.date,
                close: Number(d.close),
                open: d.open,
                high: d.high,
                low: d.low,
                volume: Number.isFinite(Number(d.volume)) ? Number(d.volume) : 0,
            })),
    };
};

function PriceTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload || {};
    return (
        <div className="price-chart-tooltip">
            <div className="price-chart-tooltip__date">{formatTickDate(label)}</div>
            <div>Close: <strong>₹{fmtPrice(row.close)}</strong></div>
            {row.high != null && <div>High: ₹{fmtPrice(row.high)}</div>}
            {row.low != null && <div>Low: ₹{fmtPrice(row.low)}</div>}
            {row.volume ? <div>Volume: {formatVolume(row.volume)}</div> : null}
        </div>
    );
}

function VolumeTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload || {};
    return (
        <div className="price-chart-tooltip">
            <div className="price-chart-tooltip__date">{formatTickDate(label)}</div>
            <div>Volume: <strong>{formatVolume(row.volume)}</strong></div>
        </div>
    );
}

function StatCard({ label, value, hint, tone }) {
    return (
        <div className={`price-stat ${tone ? `price-stat--${tone}` : ''}`}>
            <div className="price-stat-label">{label}</div>
            <div className="price-stat-value">{value}</div>
            {hint ? <div className="price-stat-hint">{hint}</div> : null}
        </div>
    );
}

function PanelMetric({ label, value, hint, tone }) {
    return (
        <div className={`price-panel-metric ${tone ? `price-stat--${tone}` : ''}`}>
            <div className="price-stat-label">{label}</div>
            <div className="price-stat-value">{value}</div>
            {hint ? <div className="price-stat-hint">{hint}</div> : null}
        </div>
    );
}

function changeTone(n) {
    if (!Number.isFinite(n) || n === 0) return undefined;
    return n > 0 ? 'up' : 'down';
}

function fromHighCopy(fromHighPct) {
    if (!Number.isFinite(fromHighPct)) return { value: '—', hint: null, tone: undefined };
    if (Math.abs(fromHighPct) < 0.05) {
        return { value: 'At the high', hint: 'Last close matches the window high', tone: undefined };
    }
    if (fromHighPct < 0) {
        return {
            value: `${Math.abs(fromHighPct).toFixed(2)}% below high`,
            hint: 'From the window peak',
            tone: 'down',
        };
    }
    return { value: fmtPct(fromHighPct), hint: 'Last close is above the recorded high', tone: 'up' };
}

export default function PriceChartTab({ companyId, currentPrice, sharesOutstanding }) {
    const [period, setPeriod] = useState(DEFAULT_PERIOD);
    const [chartData, setChartData] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const seqRef = useRef(0);

    useEffect(() => {
        if (!companyId) return undefined;
        const controller = new AbortController();
        const seq = ++seqRef.current;
        setLoading(true);
        setError(null);

        getCompanyPriceHistory(companyId, period, { signal: controller.signal })
            .then((raw) => {
                if (seq !== seqRef.current) return;
                const { result, chartData: rows } = toChartRows(raw);
                setChartData(rows);
                setMeta(result);
            })
            .catch((err) => {
                if (seq !== seqRef.current) return;
                if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
                setChartData([]);
                setMeta(null);
                if (err?.response?.status === 404) {
                    setError(null);
                    return;
                }
                setError(err?.message || 'Failed to load price history');
            })
            .finally(() => {
                if (seq === seqRef.current) setLoading(false);
            });

        return () => controller.abort();
    }, [companyId, period]);

    const handleRefresh = async () => {
        if (!companyId || refreshing) return;
        setRefreshing(true);
        setError(null);
        try {
            await refreshCompanyPrice(companyId);
            const raw = await getCompanyPriceHistory(companyId, period);
            const { result, chartData: rows } = toChartRows(raw);
            setChartData(rows);
            setMeta(result);
        } catch (err) {
            setError(err?.message || 'Failed to refresh price');
        } finally {
            setRefreshing(false);
        }
    };

    const stats = useMemo(
        () => computePriceStats(chartData, { period, currentPrice, sharesOutstanding }),
        [chartData, period, currentPrice, sharesOutstanding]
    );

    const hasVolume = chartData.some((d) => Number.isFinite(Number(d.volume)) && Number(d.volume) > 0);
    const fromHigh = fromHighCopy(stats?.fromHighPct);
    const lastCloseDiffers =
        stats &&
        Number.isFinite(Number(currentPrice)) &&
        Math.abs(Number(currentPrice) - stats.last) / Math.max(stats.last, 1) > 0.002;

    return (
        <div className="price-chart-tab">
            <div className="price-chart-toolbar">
                <div className="price-chart-periods">
                    {PERIODS.map((p) => (
                        <button
                            key={p}
                            type="button"
                            className={`price-period-btn ${period === p ? 'price-period-btn--active' : ''}`}
                            onClick={() => setPeriod(p)}
                        >
                            {p === 'MAX' ? 'Max' : p}
                        </button>
                    ))}
                </div>
                <div className="price-chart-meta">
                    <span className="price-chart-updated">
                        Last updated: {formatUpdated(meta?.lastUpdated)}
                    </span>
                    <button
                        type="button"
                        className="price-refresh-btn"
                        onClick={handleRefresh}
                        disabled={refreshing || loading}
                    >
                        {refreshing ? 'Refreshing…' : 'Refresh'}
                    </button>
                </div>
            </div>

            {stats && !loading && (
                <div className="price-kpis">
                    <div className={`price-kpi-strip${stats.cagr != null ? ' price-kpi-strip--with-cagr' : ''}`}>
                        <StatCard
                            label="Current market price"
                            value={`₹${fmtPrice(stats.cmp)}`}
                            hint={lastCloseDiffers ? `Last close ₹${fmtPrice(stats.last)}` : null}
                        />
                        <StatCard
                            label="vs prior close"
                            value={fmtPct(stats.dayChangePct)}
                            hint={stats.dayChange != null ? `₹${fmtPrice(stats.dayChange)}` : null}
                            tone={changeTone(stats.dayChangePct)}
                        />
                        <StatCard
                            label="Period return"
                            value={fmtPct(stats.periodChangePct)}
                            hint={`₹${fmtPrice(stats.periodChange)}`}
                            tone={changeTone(stats.periodChangePct)}
                        />
                        {stats.cagr != null && (
                            <StatCard
                                label="CAGR"
                                value={fmtPct(stats.cagr)}
                                hint="Compounded over this window"
                                tone={changeTone(stats.cagr)}
                            />
                        )}
                        <StatCard
                            label="Where in the range"
                            value={stats.rangeBias || '—'}
                            hint={`${Math.round(stats.rangePosPct)}% of high–low range`}
                        />
                    </div>
                    <div className="price-kpi-panels">
                        <section className="price-panel">
                            <h3 className="price-panel-title">Range</h3>
                            <div className="price-panel-body">
                                <PanelMetric
                                    label={stats.highLabel}
                                    value={`₹${fmtPrice(stats.periodHigh)}`}
                                    hint={stats.mcapAtHigh != null ? `MCap ${formatMarketCapFromRupees(stats.mcapAtHigh)}` : null}
                                />
                                <PanelMetric
                                    label={stats.lowLabel}
                                    value={`₹${fmtPrice(stats.periodLow)}`}
                                    hint={stats.mcapAtLow != null ? `MCap ${formatMarketCapFromRupees(stats.mcapAtLow)}` : null}
                                />
                            </div>
                        </section>
                        <section className="price-panel">
                            <h3 className="price-panel-title">Drawdown</h3>
                            <div className="price-panel-body">
                                <PanelMetric
                                    label="Distance from high"
                                    value={fromHigh.value}
                                    hint={fromHigh.hint}
                                    tone={fromHigh.tone}
                                />
                                <PanelMetric
                                    label="Max drawdown"
                                    value={fmtPct(stats.maxDrawdownPct)}
                                    hint="Peak to trough"
                                    tone={stats.maxDrawdownPct < 0 ? 'down' : undefined}
                                />
                            </div>
                        </section>
                        <section className="price-panel">
                            <h3 className="price-panel-title">Liquidity</h3>
                            <div className="price-panel-body">
                                <PanelMetric
                                    label="Avg daily volume"
                                    value={formatVolume(stats.avgVolume)}
                                    hint="Shares traded per day"
                                />
                                <PanelMetric
                                    label="Last session"
                                    value={formatVolume(stats.lastVolume)}
                                    hint={stats.lastVolume != null ? 'Most recent bar' : null}
                                />
                            </div>
                        </section>
                    </div>
                </div>
            )}

            <div className={`price-chart-stack ${hasVolume && !loading && chartData.length ? 'price-chart-stack--with-volume' : ''}`}>
                <div className="price-chart-container price-chart-container--price">
                    {loading ? (
                        <div className="price-chart-status">Loading price history…</div>
                    ) : error ? (
                        <div className="price-chart-status">{error}</div>
                    ) : chartData.length === 0 ? (
                        <div className="price-chart-status">No price history available for this period.</div>
                    ) : (
                        <>
                            {stats && (
                                <div className="price-chart-legend" aria-hidden="true">
                                    <span className="price-chart-legend__item price-chart-legend__item--high">
                                        High ₹{fmtPrice(stats.periodHigh)}
                                    </span>
                                    <span className="price-chart-legend__item price-chart-legend__item--low">
                                        Low ₹{fmtPrice(stats.periodLow)}
                                    </span>
                                </div>
                            )}
                            <div className="price-chart-plot">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart
                                        data={chartData}
                                        syncId="companyPrice"
                                        margin={{ top: 12, right: 12, left: 4, bottom: hasVolume ? 0 : 8 }}
                                    >
                                    <defs>
                                        <linearGradient id="companyPriceGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="var(--brand)" stopOpacity={0.3} />
                                            <stop offset="95%" stopColor="var(--brand)" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                                    <XAxis
                                        dataKey="date"
                                        hide={hasVolume}
                                        tickFormatter={formatTickDate}
                                        tick={{ fontSize: 11, fill: 'var(--text-2)' }}
                                        axisLine={{ stroke: 'var(--border)' }}
                                        tickLine={false}
                                        minTickGap={48}
                                    />
                                    <YAxis
                                        domain={['auto', 'auto']}
                                        tick={{ fontSize: 11, fill: 'var(--text-2)' }}
                                        axisLine={false}
                                        tickLine={false}
                                        tickFormatter={fmtPrice}
                                        width={68}
                                    />
                                    <Tooltip content={<PriceTooltip />} />
                                    {stats && (
                                        <ReferenceLine
                                            y={stats.periodHigh}
                                            stroke="var(--danger, #dc2626)"
                                            strokeDasharray="4 4"
                                            strokeOpacity={0.55}
                                        />
                                    )}
                                    {stats && (
                                        <ReferenceLine
                                            y={stats.periodLow}
                                            stroke="var(--success, #16a34a)"
                                            strokeDasharray="4 4"
                                            strokeOpacity={0.55}
                                        />
                                    )}
                                    <Area
                                        type="monotone"
                                        dataKey="close"
                                        name="Close"
                                        stroke="var(--brand)"
                                        strokeWidth={2}
                                        fillOpacity={1}
                                        fill="url(#companyPriceGrad)"
                                        dot={false}
                                        activeDot={{ r: 4 }}
                                    />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </>
                    )}
                </div>
                {hasVolume && !loading && chartData.length > 0 && (
                    <div className="price-chart-container price-chart-container--volume">
                        <div className="price-chart-volume-label">Volume</div>
                        <div className="price-chart-plot">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={chartData}
                                    syncId="companyPrice"
                                    margin={{ top: 8, right: 12, left: 4, bottom: 4 }}
                                >
                                <XAxis
                                    dataKey="date"
                                    tickFormatter={formatTickDate}
                                    tick={{ fontSize: 11, fill: 'var(--text-2)' }}
                                    axisLine={{ stroke: 'var(--border)' }}
                                    tickLine={false}
                                    minTickGap={48}
                                />
                                <YAxis
                                    tick={{ fontSize: 10, fill: 'var(--text-2)' }}
                                    axisLine={false}
                                    tickLine={false}
                                    tickFormatter={(v) => (v ? formatVolume(v) : '')}
                                    width={68}
                                />
                                <Tooltip content={<VolumeTooltip />} cursor={false} />
                                <Bar
                                    dataKey="volume"
                                    name="Volume"
                                    fill="var(--brand)"
                                    fillOpacity={0.38}
                                    radius={[2, 2, 0, 0]}
                                    activeBar={{
                                        fill: 'color-mix(in srgb, var(--brand) 78%, white)',
                                        fillOpacity: 1,
                                        stroke: 'var(--brand)',
                                        strokeWidth: 1,
                                        radius: [2, 2, 0, 0],
                                    }}
                                />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}
            </div>

            {stats && !loading && (
                <p className="price-chart-caption">
                    High, low, and drawdown describe how the market priced the stock in this window.
                    They are not a view on intrinsic value — use Valuation and DCF for that.
                    {meta?.source ? ` Source: ${meta.source}.` : ''}
                </p>
            )}
        </div>
    );
}
